import { formatDate } from "@angular/common";
import { Component, Inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { appendRowNodeIdToInputAriaLabel } from "@coso/grid";
import { LoadingService } from "@coso/loading";
import { DialogComponent, DialogType, IS_SAAS } from "@coso/utility";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { GridOptions, SelectionChangedEvent } from "ag-grid-community";
import { PageChangedEvent } from "ngx-bootstrap/pagination";
import { forkJoin } from "rxjs";
import { IEntityInfo, ISharePointLocationHolder } from "../../models/auto-generated";
import { AUserPermissionsService } from "../../services/a-user-permissions.service";
import { EntitiesService } from "../../services/entities.service";
import { ReplicationService } from "../../services/replication.service";
import { handleError } from "../../utils/handle-error";
import { isRunning } from "../../utils/is-running";
import { SharePointDocumentLocationsService } from "./services/sharepoint-document-locations.service";

@Component({
  standalone:false,
  templateUrl: "./overwrite-folder-permissions.component.html",
  styleUrls: ["./overwrite-folder-permissions.component.scss"]
})
export class OverwriteFolderPermissionsComponent implements OnInit {
  instanceId: string;
  @ViewChild("message") messageRef: TemplateRef<any>;
  isReadOnly: boolean;
  errorMessage: string;
  gridOptions: GridOptions;
  selectedLocations: string[] = [];

  @ViewChild("advancedFilters", { static: true }) advancedFilters: TemplateRef<any>;
  quickFilterText = "";
  filtersFormGroup: UntypedFormGroup;
  private entityFilterText = "";
  entities: IEntityInfo[];

  items: ISharePointLocationHolder[] = [];
  itemsPerPage = 10;
  totalNumberOfItems: number;
  recordsTotal: number;

  constructor(
    @Inject(IS_SAAS) public isSaaS: boolean,
    public loadingService: LoadingService,
    private fb: UntypedFormBuilder,
    private entitiesService: EntitiesService,
    private spDocLocationService: SharePointDocumentLocationsService,
    private userPermissionsService: AUserPermissionsService,
    private replicationService: ReplicationService,
    private route: ActivatedRoute,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.instanceId = this.route.snapshot.paramMap.get("instance-id");
    this.filtersFormGroup = this.fb.group({ entity: this.fb.control("") });
    this.gridOptions = {
      rowHeight: 40,
      rowSelection: "multiple",
      suppressRowClickSelection: true,
      isRowSelectable: this.isRowSelectable.bind(this),
      onSelectionChanged: this.onSelectionChanged.bind(this),
      onFirstDataRendered: (e) => {
        e.api.setGridAriaProperty("label", "List of SharePoint document locations");
        e.api.forEachNode(appendRowNodeIdToInputAriaLabel);
      },
      defaultColDef: { minWidth: 100, resizable: true, sortable: false },
      getRowId: (params) => params.data.crmEntityId,
      columnDefs: [
        {
          headerName: "Object name",
          field: "crmEntityName",
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          checkboxSelection: true,
          cellRenderer: (params) => this.entities.find((element) => element.logicalName === params.value).displayName
        },
        { headerName: "Object Id", field: "crmEntityId" },
        { headerName: "URL", field: "url" },
        { headerName: "Location Id", field: "documentLocationId" },
        {
          headerName: "Last replicated on",
          field: "lastModifiedOn",
          cellRenderer: (params) => this.formatDate(params.value)
        }
      ]
    };
    forkJoin([this.replicationService.getStatus(this.instanceId), this.userPermissionsService.hasLimitedAccess$, this.userPermissionsService.isCustomerAgent$]).subscribe(
      ([status, hasLimitedAccess, isCustomerAgent]) => (this.isReadOnly = hasLimitedAccess || !isRunning(status) || isCustomerAgent),
      (error) => (this.errorMessage = `Something went wrong due to: ${handleError(error)}`)
    );
    this.loadEntities();
    this.loadSharePointDocumentLocations(1, this.itemsPerPage);
  }

  onPaginationChanged(event: PageChangedEvent): void {
    this.loadSharePointDocumentLocations(event.page, event.itemsPerPage);
  }

  onQuickFilterChanged() {
    this.loadSharePointDocumentLocations(1, this.itemsPerPage);
  }

  onQuickFilterClear() {
    this.quickFilterText = "";
    this.loadSharePointDocumentLocations(1, this.itemsPerPage);
  }

  onPanelOpen() {
    this.filtersFormGroup.patchValue({ entity: this.entityFilterText });
    this.filtersFormGroup.updateValueAndValidity();
    const modalRef = this.modalService.open(DialogComponent, { ariaLabelledBy: "modalTitle", keyboard: true, modalDialogClass: "modal-dialog-right" });
    modalRef.componentInstance.options = {
      titleText: "Advanced Filters",
      contentTemplateRef: this.advancedFilters,
      leftButtons: [{ label: "Apply", type: "submit", class: "btn btn-primary", form: "advancedFiltersForm", onClick: this.applyFilters.bind(this) }],
      rightButtons: [
        { label: "Reset", type: "button", class: "btn btn-danger mr-2", onClick: this.resetFilters.bind(this) },
        { label: "Cancel", type: "button", class: "btn btn-dark", onClick: this.closeFiltersPanel.bind(this) }
      ]
    };
  }

  private closeFiltersPanel(modalRef: NgbActiveModal) {
    modalRef.dismiss();
  }

  private applyFilters(modalRef: NgbActiveModal) {
    modalRef.dismiss();
    const model = this.filtersFormGroup.getRawValue();
    this.entityFilterText = model.entity;
    this.loadSharePointDocumentLocations(1, this.itemsPerPage);
  }

  private resetFilters(modalRef: NgbActiveModal) {
    modalRef.dismiss();
    this.entityFilterText = "";
    this.loadSharePointDocumentLocations(1, this.itemsPerPage);
  }

  overwritePermissions() {
    this.errorMessage = undefined;
    this.loadingService.start("Overwriting folder permissions", "overwrite");
    this.spDocLocationService.overwritePermissions(this.instanceId, this.selectedLocations).subscribe(
      () => {
        const modalRef = this.modalService.open(DialogComponent, {
          keyboard: false,
          ariaLabelledBy: "modalTitle",
          windowClass: "modal-dialog-scrollable",
          scrollable: true
        });
        modalRef.componentInstance.options = {
          titleText: "Scheduled folder permissions overwrite",
          type: DialogType.Success,
          contentTemplateRef: this.messageRef,                    
          rightButtons: [
            {
              label: "OK",
              type: "button",
              class: "btn btn-primary",
              onClick: () => {
                this.loadSharePointDocumentLocations(1, this.itemsPerPage);
                modalRef.dismiss();
              }
            }
          ],
          onClose: () => modalRef.dismiss()
        };
        this.loadingService.stop("overwrite");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("overwrite");
      }
    );
  }

  private isRowSelectable() {
    return !this.isReadOnly;
  }

  private onSelectionChanged(e: SelectionChangedEvent) {
    e.api.forEachNode(appendRowNodeIdToInputAriaLabel);
    const rows = e.api.getSelectedRows();
    this.selectedLocations = rows.map((r) => r.documentLocationId);
  }

  private formatDate(date: string) {    
    if (date === null || date === "0001-01-01T00:00:00") return "Unavailable";
    return formatDate(date, "MMM d, y HH:mm:ss", "en-US");
  }

  private loadEntities() {
    this.loadingService.start("Loading Dynamics 365 entities", "loadEntities");
    this.entitiesService.get(this.instanceId).subscribe(
      (data) => {
        this.entities = data;
        this.entities.unshift({ displayName: "All", logicalName: "", isActivity: false });
        this.loadingService.stop("loadEntities");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("loadEntities");
      }
    );
  }

  private loadSharePointDocumentLocations(page: number, pageSize: number) {
    this.loadingService.start("Loading Dynamics 365 SharePoint Document Locations", "loadSharePointDocumentLocations");
    this.itemsPerPage = pageSize;
    this.spDocLocationService.get(this.instanceId, pageSize, pageSize * (page - 1), this.quickFilterText, this.entityFilterText).subscribe(
      (data) => {
        this.selectedLocations = [];
        this.items = data.sharePointLocations;
        this.totalNumberOfItems = data.total;
        if (!this.quickFilterText && !this.entityFilterText) {
          this.recordsTotal = data.total;
        }
        this.loadingService.stop("loadSharePointDocumentLocations");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("loadSharePointDocumentLocations");
      }
    );
  }
}
